import { AlertCircle, ShieldCheck, Smartphone } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaOtpInput } from "@/shared/components/forms/ColocaOtpInput";
import useTranslations from "@/shared/lang/useTranslations";

interface TwoFAPageProps {
  onSubmit?: (code: string) => void;
  isLoading?: boolean;
  error?: string;
  onBackToLogin?: () => void;
}

export const TwoFAPage = ({ onSubmit, isLoading = false, error, onBackToLogin }: TwoFAPageProps) => {
  const [value, setValue] = useState("");
  const { t } = useTranslations("login");

  const handleSubmit = () => {
    if (value.length === 6 && onSubmit) {
      onSubmit(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && value.length === 6) {
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col items-center w-full max-w-md mx-auto">
      {/* Header */}
      <div className="flex flex-col items-center gap-4 mb-8 animate-fade-down">
        <div className="relative">
          <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
          <div className="relative bg-primary/10 p-2 rounded-full">
            <ShieldCheck className="w-8 h-8 text-primary" />
          </div>
        </div>

        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-foreground">{t("twofa_title")}</h2>
          <p className="text-sm text-muted-foreground max-w-sm">{t("twofa_subtitle")}</p>
        </div>
      </div>

      {/* Authenticator app hint */}
      <div className="flex items-center gap-2 mb-6 px-4 py-3 bg-muted/50 rounded-lg border border-border animate-fade-down animation-delay-100">
        <Smartphone className="w-5 h-5 text-primary shrink-0" />
        <p className="text-xs text-muted-foreground">{t("twofa_authenticator_hint")}</p>
      </div>

      {/* OTP + actions */}
      <div className="flex flex-col gap-6 w-full animate-fade-up animation-delay-200">
        <div className="flex flex-col items-center gap-3 w-full">
          <div onKeyDown={handleKeyDown}>
            <ColocaOtpInput
              autoFocus
              value={value}
              onValueChange={setValue}
              disabled={isLoading}
              invalid={!!error}
              aria-label={t("twofa_title")}
              onComplete={handleSubmit}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            {t("twofa_digits_counter").replace("{count}", value.length.toString())}
          </p>
        </div>

        {error && (
          <div className="animate-shake">
            <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />
          </div>
        )}

        <ColocaButton
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={value.length !== 6 || isLoading}
          className="h-12 w-full text-base font-semibold"
        >
          {isLoading ? t("twofa_verifying_button") : t("twofa_verify_button")}
        </ColocaButton>

        {/* Footer */}
        <div className="text-center space-y-3 pt-6 border-t border-border">
          <p className="text-xs text-muted-foreground">{t("twofa_support_text")}</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a
              href="mailto:support@colocapayments.com"
              className="text-xs font-semibold cursor-pointer relative inline-block group transition-all duration-300 hover:text-primary py-1"
            >
              {t("twofa_support_link")}
              <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-primary rounded-full transition-all duration-300 group-hover:w-full"></span>
            </a>
            <span className="text-xs text-muted-foreground">•</span>
            <button
              type="button"
              onClick={onBackToLogin}
              disabled={isLoading}
              className="text-xs font-semibold cursor-pointer relative inline-block group transition-all duration-300 hover:text-primary py-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("twofa_back_link")}
              <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-primary rounded-full transition-all duration-300 group-hover:w-full"></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
